import { memo } from 'react';
import getSymbolFromCurrency from 'currency-symbol-map';
import { brightenColor } from 'src/customizable-ui/dashboard/utils/brightenColor.js';

const YourReferralsCard = memo(
	({
		card,
		isEditMode = true,
		isDragging = false,
		isResizing = false,
		isHovered = false,
		userStats,
		isAdminView = false,
	}) => {
		// Sample values so admins can lay the card out before stats load; real
		// users get zeroes rather than fake numbers while the query is in flight.
		const referralsCount = userStats?.referralTotal ?? (isAdminView ? 12 : 0);
		const totalBonuses = userStats?.bonusTotal ?? (isAdminView ? 3450 : 0);
		const currencySymbol = userStats?.currency
			? getSymbolFromCurrency(userStats.currency)
			: '$';
		const cardData = card.data || {};
		const title = cardData.title || 'Your Referrals';
		const backgroundColor = cardData.backgroundColor || '#ffffff';
		const borderColor = cardData.borderColor || '#dddddd';
		const textColor = cardData.textColor || '#595959';
		const showBorder = cardData.showBorder !== false;
		const borderSize = cardData.borderSize || 2;
		const referralsBackgroundColor =
			cardData.referralsBackgroundColor || '#ffffff';
		const referralsBorderColor = cardData.referralsBorderColor || '#ffffff';
		const referralsTextColor = cardData.referralsTextColor || '#000000';
		const referralsTotalColor = cardData.referralsTotalColor || '#0686d0';
		const bonusesBackgroundColor = cardData.bonusesBackgroundColor || '#6cb761';
		const bonusesBorderColor = cardData.bonusesBorderColor || '#6cb761';
		const bonusesTextColor = cardData.bonusesTextColor || '#ffffff';
		const bonusesTotalColor = cardData.bonusesTotalColor || '#ffffff';

		const isStacked = card.height > 1;

		const getCardBorderStyle = () => {
			if (!showBorder && !isEditMode) return 'none';

			if (isDragging || isResizing) {
				return `${borderSize}px solid #74a9ffff`;
			}

			if (isEditMode && isHovered && !isDragging && !isResizing) {
				const brighterBorder = brightenColor(borderColor || '#dddddd', 30);
				return `${borderSize}px solid ${brighterBorder}`;
			}

			if (showBorder) {
				return `${borderSize}px solid ${borderColor}`;
			}

			return 'none';
		};

		const getCardShadow = () => {
			if (isDragging || isResizing) {
				return '0 25px 50px -12px rgba(0, 0, 0, 0.25)';
			}
			if (isEditMode && isHovered && !isDragging && !isResizing) {
				return '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
			}
			return 'none';
		};

		const StatBox = ({
			value,
			label,
			bgColor,
			borderColor: boxBorderColor,
			labelTextColor,
			totalColor,
		}) => (
			<div
				className="flex flex-col items-center justify-center p-4 flex-1"
				style={{
					backgroundColor: bgColor,
					border: `1px solid ${boxBorderColor}`,
					minHeight: isStacked ? '80px' : 'auto',
					boxShadow: '0 0 16px rgba(0,0,0,.051)',
					borderRadius: '14px',
				}}
			>
				<div
					className="text-3xl font-bold mb-1"
					style={{
						color: totalColor,
						fontFamily: 'Nunito Sans, sans-serif',
					}}
				>
					{label === 'Total Bonuses'
						? `${currencySymbol}${value.toLocaleString('en-US')}`
						: value.toLocaleString()}
				</div>
				<div
					className="font-bold opacity-70"
					style={{
						color: labelTextColor,
						fontFamily: 'Nunito Sans, sans-serif',
						fontSize: '14px',
					}}
				>
					{label}
				</div>
			</div>
		);

		return (
			<div
				className="w-full h-full flex flex-col relative transition-all duration-200"
				style={{
					background: backgroundColor,
					border: getCardBorderStyle(),
					boxShadow: getCardShadow(),
					transform: isDragging ? 'scale(1.05)' : 'scale(1)',
					borderRadius: '14px',
					padding: '16px',
				}}
			>
				<div className="d-card-head">
					<h4
						style={{
							fontSize: '14px',
							fontWeight: 700,
							lineHeight: '18px',
							margin: '0 0 10px',
							color: textColor,
						}}
					>
						{title}
					</h4>
				</div>
				<div
					className={`flex ${isStacked ? 'flex-col' : 'flex-row'} gap-3 flex-1`}
					style={{
						minHeight: 0,
					}}
				>
					<StatBox
						value={referralsCount}
						label="Referrals"
						bgColor={referralsBackgroundColor}
						borderColor={referralsBorderColor}
						labelTextColor={referralsTextColor}
						totalColor={referralsTotalColor}
					/>
					<StatBox
						value={totalBonuses}
						label="Total Bonuses"
						bgColor={bonusesBackgroundColor}
						borderColor={bonusesBorderColor}
						labelTextColor={bonusesTextColor}
						totalColor={bonusesTotalColor}
					/>
				</div>
			</div>
		);
	}
);

YourReferralsCard.displayName = 'YourReferralsCard';

export default YourReferralsCard;
